import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/lib/api';
import type { ScmPlatform } from '@codemerlin/shared';

export interface PlatformMeResponse {
  email: string;
  role: string;
  userId: string;
}

export function usePlatformMe() {
  return useQuery({
    queryKey: ['platform', 'me'],
    queryFn: () => apiClient<PlatformMeResponse>('platform/me'),
    retry: false,
  });
}

export interface OrganizationSummaryRow {
  id: string;
  name: string;
  /** DC-478 — company the tenant works for; null for orgs onboarded before DC-478 */
  company_name: string | null;
  status: string;
  billing_model: string;
  current_rate_per_100_loc_usd: number;
  is_custom_rate: boolean;
  current_period_loc_processed: number;
  current_period_billed_cost_usd: number;
  current_period_actual_llm_cost_usd: number;
  gross_margin_usd: number;
  last_login_at: string | null;
  github_connected: boolean;
  scm_connections: Record<ScmPlatform, boolean>;
  reviews_completed_30d: number;
  onboarding_completed_at: string | null;
  returned_after_setup: boolean | null;
  created_at: string;
  deleted_at: string | null;
  purge_eligible_at: string | null;
}

export interface PlatformOrganizationsResponse {
  data: OrganizationSummaryRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface SpendTrendPoint {
  date: string;
  locProcessed: number;
  billedCostUsd: number;
  actualLlmCostUsd: number;
}

export interface BillingRateHistoryRow {
  previous_rate: number;
  new_rate: number;
  changed_by_email: string;
  reason: string | null;
  created_at: string;
}

export interface AdminMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: string;
  joined_at: string;
  last_login_at: string | null;
  /** DC-326 — operator-granted Next shell preview */
  next_shell_preview?: boolean;
}

export interface SubscriptionSummary {
  tier: string;
  status: string;
  current_period_end: string;
  trial_end_date: string | null;
  cancel_at_period_end: boolean;
}

export interface GithubInstallationStatus {
  connected: boolean;
  installation_id: number | null;
}

export interface RecentAuditActivityRow {
  id: string;
  action: string;
  actor_email: string | null;
  created_at: string;
}

export interface PlatformOrganizationDetail {
  id: string;
  name: string;
  /** DC-478 — company the tenant works for; null for orgs onboarded before DC-478 */
  company_name: string | null;
  status: string;
  deleted_at: string | null;
  deleted_by: string | null;
  purge_eligible_at: string | null;
  billing_model: string;
  current_rate_per_100_loc_usd: number;
  is_custom_rate: boolean;
  free_review_limit: number;
  billing_setup_completed_at: string | null;
  billing_collection_ready_at: string | null;
  billing_collection_failed_at: string | null;
  current_period_loc_processed: number;
  current_period_billed_cost_usd: number;
  current_period_actual_llm_cost_usd: number;
  gross_margin_usd: number;
  last_login_at: string | null;
  created_at: string;
  spend_trend: SpendTrendPoint[];
  billing_rate_history: BillingRateHistoryRow[];
  account_creator: AdminMember | null;
  admin_members: AdminMember[];
  stripe_payment_setup: boolean;
  subscription_summary: SubscriptionSummary | null;
  github_installation: GithubInstallationStatus;
  recent_audit_activity: RecentAuditActivityRow[];
  /** DC-326 — Sorcery rollout mirrors platform GET …/next-navigation */
  next_navigation_org_enabled: boolean;
  pr_presentation_independent: boolean;
}

export function usePlatformOrganizations(params: {
  search?: string;
  status?: string;
  scm?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) {
  const sp = new URLSearchParams();
  if (params.search) sp.set('search', params.search);
  if (params.status) sp.set('status', params.status);
  if (params.scm) sp.set('scm', params.scm);
  if (params.sort) sp.set('sort', params.sort);
  if (params.direction) sp.set('direction', params.direction);
  if (params.limit !== undefined && params.limit !== null) sp.set('limit', String(params.limit));
  if (params.offset !== undefined && params.offset !== null)
    sp.set('offset', String(params.offset));
  const q = sp.toString();
  return useQuery({
    queryKey: ['platform', 'organizations', params],
    queryFn: () =>
      apiClient<PlatformOrganizationsResponse>(`platform/organizations${q ? `?${q}` : ''}`),
  });
}

export function usePlatformOrganizationDetail(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'organization', organizationId],
    queryFn: () =>
      apiClient<PlatformOrganizationDetail>(`platform/organizations/${organizationId as string}`),
    enabled: Boolean(organizationId),
  });
}

export interface ForgeJiraTestCommentResponse {
  work_item_id: string;
  issue_key: string;
  forge_status: number;
  forge_body: unknown;
}

export function usePostForgeJiraTestCommentLatestWorkItem(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<ForgeJiraTestCommentResponse>(
        `platform/organizations/${organizationId as string}/forge-jira/test-comment-latest-work-item`,
        { method: 'POST', body: JSON.stringify({}) }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

export interface PlatformDemoBoundaryResponse {
  organization_id: string;
  demo_designated: boolean;
  demo_config: {
    master_demo_enabled: boolean;
    next_shell_enabled: boolean;
    allowlisted_project_key: string;
    demo_scenario_id: string;
  } | null;
  demo_eligible: boolean;
}

export function usePlatformDemoBoundary(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'demo-boundary', organizationId],
    queryFn: () =>
      apiClient<PlatformDemoBoundaryResponse>(
        `platform/organizations/${organizationId as string}/demo-boundary`
      ),
    enabled: Boolean(organizationId),
  });
}

export function usePatchPlatformDemoBoundary(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      demo_designated?: boolean;
      demo_config?: PlatformDemoBoundaryResponse['demo_config'] | null;
    }) =>
      apiClient(`platform/organizations/${organizationId as string}/demo-boundary`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'demo-boundary', organizationId] });
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

export interface DemoOperationAcceptedResponse {
  operation_id: string;
  correlation_id: string;
  status: 'pending';
}

export interface DemoOperationStatusResponse {
  operation_id: string;
  correlation_id: string;
  organization_id: string;
  kind: string;
  status: string;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  failure_code: string | null;
  failure_message: string | null;
  details: Record<string, unknown> | null;
}

export function usePostPlatformDemoReset(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: { idempotency_key?: string }) =>
      apiClient<DemoOperationAcceptedResponse>(
        `platform/organizations/${organizationId as string}/demo-reset`,
        {
          method: 'POST',
          body: JSON.stringify(body ?? {}),
        }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

export function usePostPlatformDemoReplay(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: { idempotency_key?: string; parameters?: Record<string, unknown> }) =>
      apiClient<DemoOperationAcceptedResponse>(
        `platform/organizations/${organizationId as string}/demo-replay`,
        {
          method: 'POST',
          body: JSON.stringify(body ?? {}),
        }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

export function usePlatformDemoOperation(
  organizationId: string | undefined,
  operationId: string | undefined
) {
  return useQuery({
    queryKey: ['platform', 'demo-operation', organizationId, operationId],
    queryFn: () =>
      apiClient<DemoOperationStatusResponse>(
        `platform/organizations/${organizationId as string}/demo-operations/${operationId as string}`
      ),
    enabled: Boolean(organizationId && operationId),
    refetchInterval: (query) => {
      const st = query.state.data?.status;
      return st === 'pending' || st === 'running' ? 2500 : false;
    },
  });
}

export function usePatchPlatformBilling(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      price_per_100_loc_usd?: number | null;
      free_review_limit?: number;
      reason?: string;
    }) =>
      apiClient(`platform/organizations/${organizationId as string}/billing`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
      await qc.invalidateQueries({ queryKey: ['platform', 'organizations'] });
    },
  });
}

export interface PlatformAuditLogRow {
  id: string;
  actor_email: string | null;
  organization_id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  changes: unknown;
  created_at: string;
}

// ---- Billing runs ----

export interface BillingRunRow {
  billing_run_id: string;
  period_start: string;
  period_end: string;
  loc_processed: number;
  effective_rate_per_100_loc_usd: number;
  amount_usd: number;
  stripe_invoice_id: string | null;
  status: string;
  attempted_at: string;
  // DC-313: override columns
  override_amount_usd: number | null;
  override_rate_per_100_loc_usd: number | null;
  override_note: string | null;
  overridden_by_email: string | null;
  overridden_at: string | null;
}

// DC-313: Billing ops preview + override + process hooks

export interface BillingPeriodPreview {
  period_start: string;
  period_end: string;
  period_label: string;
  is_period_closed: boolean;
  loc_processed: number;
  base_rate_per_100_loc_usd: number;
  effective_rate_per_100_loc_usd: number;
  computed_amount_usd: number;
  effective_amount_usd: number;
  existing_run_id?: string | null;
  existing_run_status?: string | null;
  stripe_invoice_id?: string | null;
  override_amount_usd?: number | null;
  override_rate_per_100_loc_usd?: number | null;
  override_note?: string | null;
  overridden_by_email?: string | null;
  overridden_at?: string | null;
}

export interface BillingRunsPreviewResponse {
  open_period: BillingPeriodPreview;
  closed_period: BillingPeriodPreview;
}

export function usePlatformBillingPreview(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'billing-runs-preview', organizationId],
    queryFn: () =>
      apiClient<BillingRunsPreviewResponse>(
        `platform/organizations/${organizationId as string}/billing-runs/preview`
      ),
    enabled: Boolean(organizationId),
    staleTime: 60_000,
  });
}

export function useSetBillingRunOverride(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      period_start: string;
      period_end: string;
      override_amount_usd?: number | null;
      override_rate_per_100_loc_usd?: number | null;
      override_note: string;
    }) =>
      apiClient(`platform/organizations/${organizationId as string}/billing-runs/override`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['platform', 'billing-runs-preview', organizationId],
      });
      await qc.invalidateQueries({ queryKey: ['platform', 'billing-runs', organizationId] });
    },
  });
}

export function useClearBillingRunOverride(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period_start: string; period_end: string }) =>
      apiClient(`platform/organizations/${organizationId as string}/billing-runs/override`, {
        method: 'DELETE',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['platform', 'billing-runs-preview', organizationId],
      });
      await qc.invalidateQueries({ queryKey: ['platform', 'billing-runs', organizationId] });
    },
  });
}

export function useProcessBillingRun(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period_start: string; period_end: string; reason: string }) =>
      apiClient<{
        success: boolean;
        status: string;
        billing_run_id?: string;
        stripe_invoice_id?: string | null;
        amount_usd?: number;
        loc_processed?: number;
      }>(`platform/organizations/${organizationId as string}/billing-runs/process`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['platform', 'billing-runs-preview', organizationId],
      });
      await qc.invalidateQueries({ queryKey: ['platform', 'billing-runs', organizationId] });
      await qc.invalidateQueries({ queryKey: ['platform', 'billing-health'] });
    },
  });
}

export function usePlatformBillingRuns(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'billing-runs', organizationId],
    queryFn: () =>
      apiClient<{ data: BillingRunRow[] }>(
        `platform/organizations/${organizationId as string}/billing-runs`
      ),
    enabled: Boolean(organizationId),
  });
}

// ---- Billing gate ----

export interface BillingGateRow {
  allowed: boolean;
  blocked_reason: string | null;
  free_review_limit: number;
  free_reviews_used: number;
  free_reviews_remaining: number | null;
  billing_ready: boolean;
  billing_failed: boolean;
  failed_at: string | null;
}

export function usePlatformBillingGate(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'billing-gate', organizationId],
    queryFn: () =>
      apiClient<BillingGateRow>(`platform/organizations/${organizationId as string}/billing-gate`),
    enabled: Boolean(organizationId),
  });
}

// ---- Budget ----

export interface OrgBudgetRow {
  hard_budget_usd: number | null;
  soft_threshold_percent: number | null;
  rate_limit_rpm: number | null;
  rate_limit_tier: string | null;
}

export function usePlatformOrgBudget(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'org-budget', organizationId],
    queryFn: () =>
      apiClient<OrgBudgetRow>(`platform/organizations/${organizationId as string}/budget`),
    enabled: Boolean(organizationId),
  });
}

// ---- Billing health ----

export interface BillingHealthTotals {
  blocked: number;
  payment_failed: number;
  billing_ready: number;
  free_tier: number;
  total: number;
}

export interface FailedRunRow {
  billing_run_id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  amount_usd: number;
  stripe_invoice_id: string | null;
  attempted_at: string;
}

export interface BillingHealthResponse {
  totals: BillingHealthTotals;
  recent_failed_runs: FailedRunRow[];
  pending_charges_summary: {
    open_amount_usd_total: number;
    actionable_closed_amount_usd_total: number;
    total_pending_amount_usd: number;
    tenants_with_pending_count: number;
  };
  pending_charges: Array<{
    organization_id: string;
    organization_name: string;
    gate_status: 'blocked' | 'payment_failed' | 'billing_ready' | 'free_tier';
    open_period_start: string;
    open_period_end: string;
    open_loc_processed: number;
    open_amount_usd: number;
    closed_period_start: string;
    closed_period_end: string;
    closed_loc_processed: number;
    closed_amount_usd: number;
    actionable_closed_amount_usd: number;
    closed_run_status: string | null;
    closed_override_amount_usd: number | null;
    closed_override_rate_per_100_loc_usd: number | null;
    stripe_invoice_id: string | null;
    total_pending_amount_usd: number;
  }>;
  last_billing_run_at: string | null;
}

export function usePlatformBillingHealth() {
  return useQuery({
    queryKey: ['platform', 'billing-health'],
    queryFn: () => apiClient<BillingHealthResponse>('platform/billing-health'),
    staleTime: 30_000,
  });
}

// ---- Integration interest ----

export interface IntegrationInterestRow {
  signal_key: string;
  click_count: number;
  updated_at: string;
}

export function usePlatformIntegrationInterest() {
  return useQuery({
    queryKey: ['platform', 'integration-interest'],
    queryFn: () => apiClient<{ data: IntegrationInterestRow[] }>('platform/integration-interest'),
  });
}

export function usePurgePlatformOrganization(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ status: string; organization_id: string }>(
        `platform/organizations/${organizationId as string}/purge`,
        { method: 'POST' }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'organizations'] });
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

// ---- Next navigation rollout (DC-326) ----

export interface PlatformNextNavigationResponse {
  organization_id: string;
  next_navigation_org_enabled: boolean;
  pr_presentation_independent: boolean;
  updated_at: string;
}

export function usePlatformNextNavigation(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'next-navigation', organizationId],
    queryFn: () =>
      apiClient<PlatformNextNavigationResponse>(
        `platform/organizations/${organizationId as string}/next-navigation`
      ),
    enabled: Boolean(organizationId),
  });
}

export function usePatchPlatformNextNavigation(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      next_navigation_org_enabled?: boolean;
      pr_presentation_independent?: boolean;
      reason: string;
    }) =>
      apiClient(`platform/organizations/${organizationId as string}/next-navigation`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'next-navigation', organizationId] });
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

export function usePatchPlatformMemberNextNavigationPreview(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: string; preview_enabled: boolean; reason: string }) =>
      apiClient(
        `platform/organizations/${organizationId as string}/members/${args.userId}/next-navigation-preview`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            preview_enabled: args.preview_enabled,
            reason: args.reason,
          }),
        }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'next-navigation', organizationId] });
      await qc.invalidateQueries({ queryKey: ['platform', 'organization', organizationId] });
    },
  });
}

export function usePlatformAuditLogs(params: { orgId?: string; limit?: number; offset?: number }) {
  const sp = new URLSearchParams();
  if (params.orgId) sp.set('orgId', params.orgId);
  if (params.limit !== undefined && params.limit !== null) sp.set('limit', String(params.limit));
  if (params.offset !== undefined && params.offset !== null)
    sp.set('offset', String(params.offset));
  const q = sp.toString();
  return useQuery({
    queryKey: ['platform', 'audit-logs', params],
    queryFn: () =>
      apiClient<{ data: PlatformAuditLogRow[]; total: number }>(
        `platform/audit-logs${q ? `?${q}` : ''}`
      ),
  });
}

// ---- SDD feature gates (Sorcery tenant matrix / history — DC-401) ----

export interface PlatformFeatureGateCatalogGate {
  gate_key: string;
  description: string;
  value_kind: string;
  default_value: unknown;
  scope_kind: string;
  control_class: string;
  customer_visible: boolean;
  entitlement_capability_key: string | null;
  deprecated_at: string | null;
  row_version: number;
  catalog_version: number;
}

export interface PlatformFeatureGateCatalogResponse {
  gates: PlatformFeatureGateCatalogGate[];
}

export function usePlatformFeatureGatesCatalog() {
  return useQuery({
    queryKey: ['platform', 'feature-gates', 'catalog'],
    queryFn: () => apiClient<PlatformFeatureGateCatalogResponse>('platform/feature-gates/catalog'),
  });
}

export interface PlatformOrgEffectiveFeatureGateRow {
  gate_key: string;
  effective_allowed: boolean;
  summary_state: 'on' | 'off' | 'entitlement_bounded';
  sources_used?: string[];
  assignment_valid_from?: string | null;
  assignment_valid_to?: string | null;
}

export interface PlatformOrgEffectiveFeatureGatesResponse {
  organization_id: string;
  gates: PlatformOrgEffectiveFeatureGateRow[];
}

export function usePlatformOrganizationFeatureGatesEffective(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'feature-gates', 'effective', organizationId],
    queryFn: () =>
      apiClient<PlatformOrgEffectiveFeatureGatesResponse>(
        `platform/organizations/${organizationId as string}/feature-gates/effective`
      ),
    enabled: Boolean(organizationId),
  });
}

export interface PlatformFeatureGateAuditEventRow {
  event_id: string;
  created_at: string;
  outcome: string;
  event_kind: string;
  gate_key: string;
  organization_id: string | null;
  actor_user_id: string | null;
}

export interface PlatformFeatureGateAuditEventsResponse {
  events: PlatformFeatureGateAuditEventRow[];
  next_cursor: string | null;
}

export interface PlatformFeatureGateAuditQuery {
  since: string;
  until: string;
  gate_key: string;
  organization_id: string;
  outcome?: 'success' | 'conflict';
  cursor?: string;
  limit?: number;
}

function buildFeatureGateAuditQueryString(q: PlatformFeatureGateAuditQuery): string {
  const sp = new URLSearchParams();
  sp.set('since', q.since);
  sp.set('until', q.until);
  sp.set('gate_key', q.gate_key);
  sp.set('organization_id', q.organization_id);
  if (q.outcome) sp.set('outcome', q.outcome);
  if (q.cursor) sp.set('cursor', q.cursor);
  if (q.limit !== undefined) sp.set('limit', String(q.limit));
  return sp.toString();
}

export function usePlatformFeatureGateAuditEvents(
  query: PlatformFeatureGateAuditQuery | undefined
) {
  return useQuery({
    queryKey: ['platform', 'feature-gates', 'audit-events', query],
    queryFn: () =>
      apiClient<PlatformFeatureGateAuditEventsResponse>(
        `platform/feature-gates/audit-events?${buildFeatureGateAuditQueryString(query!)}`
      ),
    enabled: Boolean(
      query &&
      query.since.length > 0 &&
      query.until.length > 0 &&
      query.gate_key.length > 0 &&
      query.organization_id.length > 0
    ),
  });
}

export interface PlatformOrgFeatureGateAssignment {
  organization_id: string;
  gate_key: string;
  rollout_value: boolean | null;
  org_operational_disabled: boolean;
  internal_override: boolean;
  row_version: number;
  control_class: string;
}

export interface PlatformGlobalFeatureGatePosture {
  gate_key: string;
  globally_disabled: boolean;
  row_version: number;
  control_class: string;
}

export async function fetchPlatformOrgFeatureGateAssignment(
  organizationId: string,
  gateKey: string
): Promise<PlatformOrgFeatureGateAssignment | null> {
  try {
    return await apiClient<PlatformOrgFeatureGateAssignment>(
      `platform/organizations/${organizationId}/feature-gates/${encodeURIComponent(gateKey)}`
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function fetchPlatformGlobalFeatureGatePosture(
  gateKey: string
): Promise<PlatformGlobalFeatureGatePosture> {
  return apiClient<PlatformGlobalFeatureGatePosture>(
    `platform/feature-gates/${encodeURIComponent(gateKey)}/global`
  );
}

export function usePatchPlatformOrgFeatureGate(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { gateKey: string; body: Record<string, unknown> }) =>
      apiClient(
        `platform/organizations/${organizationId as string}/feature-gates/${encodeURIComponent(args.gateKey)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(args.body),
        }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['platform', 'feature-gates', 'effective', organizationId],
      });
      await qc.invalidateQueries({ queryKey: ['platform', 'feature-gates', 'audit-events'] });
    },
  });
}

export function usePatchPlatformGlobalFeatureGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { gateKey: string; body: Record<string, unknown> }) =>
      apiClient(`platform/feature-gates/${encodeURIComponent(args.gateKey)}/global`, {
        method: 'PATCH',
        body: JSON.stringify(args.body),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platform', 'feature-gates'] });
      await qc.invalidateQueries({ queryKey: ['platform', 'feature-gates', 'global-postures'] });
    },
  });
}

export interface PlatformFeatureGatesGlobalPosturesResponse {
  gates: Array<{
    gate_key: string;
    description: string;
    control_class: string;
    globally_disabled: boolean;
    row_version: number;
    updated_at: string;
    catalog_version_at_commit: number;
    catalog_version: number;
  }>;
}

export function usePlatformFeatureGatesGlobalPostures() {
  return useQuery({
    queryKey: ['platform', 'feature-gates', 'global-postures'],
    queryFn: () =>
      apiClient<PlatformFeatureGatesGlobalPosturesResponse>(
        'platform/feature-gates/global-postures'
      ),
  });
}

export function usePostPlatformOrgFeatureGateRollback(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      gateKey: string;
      body: { expected_row_version: number; reason: string };
    }) =>
      apiClient(
        `platform/organizations/${organizationId as string}/feature-gates/${encodeURIComponent(args.gateKey)}/rollback`,
        {
          method: 'POST',
          body: JSON.stringify(args.body),
        }
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['platform', 'feature-gates', 'effective', organizationId],
      });
      await qc.invalidateQueries({ queryKey: ['platform', 'feature-gates', 'audit-events'] });
    },
  });
}

// ---- SDD spec pipeline (operator intake) ----

export type SpecPipelinePreflightCheck = {
  id: string;
  status: 'pass' | 'warning' | 'blocked';
  message: string;
  detail?: Record<string, unknown>;
};

export type SpecPipelineIntakePreflightResult = {
  status: 'pass' | 'warning' | 'blocked';
  checks: SpecPipelinePreflightCheck[];
  envelope_valid: boolean;
};

export type SpecPipelineIntakeResult = {
  intake_decision_id: string;
  outcome: string;
  reason_code: string;
  correlation_id: string;
  generation_attempt_id: string | null;
  provider_capability_check?: Record<string, unknown>;
  work_item_id?: string;
  work_item_materialization?: {
    outcome: string;
    canonical_external_id: string;
  };
};

export function useSpecPipelineIntakePreflight(organizationId: string | undefined) {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient<SpecPipelineIntakePreflightResult>(
        `platform/organizations/${organizationId as string}/spec-pipeline/intake-preflight`,
        { method: 'POST', body: JSON.stringify(body) }
      ),
  });
}

export function useSpecPipelineIntake(organizationId: string | undefined) {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient<SpecPipelineIntakeResult>(
        `platform/organizations/${organizationId as string}/spec-pipeline/intake`,
        { method: 'POST', body: JSON.stringify(body) }
      ),
  });
}

export function useSpecPipelineIntakeDecisions(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'spec-pipeline', 'intake-decisions', organizationId],
    queryFn: () =>
      apiClient<{
        entries: Array<{
          id: string;
          created_at: string;
          outcome: string;
          reason_code: string;
          vendor_kind: string;
          issue_key: string | null;
          correlation_id: string;
          generation_gates?: {
            outcome: string;
            primary_reason_family: string;
            policy_version: string | null;
            evaluated_at: string;
          };
        }>;
      }>(
        `platform/organizations/${organizationId as string}/spec-pipeline/intake-decisions?limit=10`
      ),
    enabled: Boolean(organizationId),
    staleTime: 15_000,
  });
}
